import React, { useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Shield, Award, Users, TrendingUp, Scale, CheckCircle2 } from 'lucide-react';
import { ConsultationForm } from '../components/ConsultationForm';
import { openChat } from '../utils/chat';

export const AboutUs = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);

  const openForm = () => {
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
  };
  const values = [
    {
      icon: Shield,
      title: 'Integrity',
      description: 'We uphold the highest standards of legal ethics and professional conduct in every case we handle.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'Our commitment to excellence drives us to deliver superior legal services and successful outcomes.'
    },
    {
      icon: Users,
      title: 'Client-Focused',
      description: 'Your success is our priority. We provide personalized attention and tailored legal strategies.'
    },
    {
      icon: TrendingUp,
      title: 'Innovation',
      description: 'We leverage modern technology and innovative approaches to streamline the IP protection process.'
    }
  ];

  const team = [
    {
      name: 'Senior IP Attorney',
      role: 'Lead Trademark Specialist',
      description: 'Extensive experience in trademark law with hundreds of successful registrations'
    },
    {
      name: 'Patent Attorney',
      role: 'Innovation Protection Expert',
      description: 'Specialized in securing patents for cutting-edge technologies and inventions'
    },
    {
      name: 'Copyright Specialist',
      role: 'Creative Rights Attorney',
      description: 'Dedicated to protecting creative works and digital content rights'
    },
    {
      name: 'Legal Strategist',
      role: 'IP Portfolio Manager',
      description: 'Expert in building comprehensive intellectual property portfolios'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar onOpenForm={openForm} />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#0A1F44] to-[#153356] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              About Otis Trademark
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Your trusted partner in intellectual property protection, dedicated to safeguarding the innovations and brands that define your success.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wider">Our Mission</span>
              <h2 className="text-4xl font-bold text-[#0A1F44] mt-4 mb-6">
                Safeguarding Innovation. Protecting Identity.
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                At Otis Trademark, we're committed to safeguarding the innovations and brands that define modern business. Our purpose is delivering accessible, specialized legal expertise that enables entrepreneurs, enterprises, and creative professionals to secure their intellectual property rights with complete confidence.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                We combine legal expertise with modern technology to deliver a seamless registration experience. Our team of experienced IP attorneys is dedicated to ensuring your trademarks, copyrights, and patents receive the protection they deserve.
              </p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-[#0A1F44] mb-2">Expert Legal Team</h3>
                    <p className="text-gray-600">Experienced IP attorneys committed to your success</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-[#0A1F44] mb-2">Transparent Process</h3>
                    <p className="text-gray-600">Clear communication and pricing at every step</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-[#0A1F44] mb-2">Comprehensive Services</h3>
                    <p className="text-gray-600">Complete IP protection from registration to enforcement</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-[#0A1F44] mb-2">Client Success</h3>
                    <p className="text-gray-600">Your satisfaction and protection are our top priorities</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wider">Our Values</span>
            <h2 className="text-4xl font-bold text-[#0A1F44] mt-4 mb-6">
              What We Stand For
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our core values guide every decision we make and every service we provide.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300">
                  <div className="w-16 h-16 bg-[#0A1F44] rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-8 h-8 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0A1F44] mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wider">Our Team</span>
            <h2 className="text-4xl font-bold text-[#0A1F44] mt-4 mb-6">
              Expert Legal Professionals
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our team of specialized attorneys brings deep expertise in intellectual property law.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-6 text-center hover:shadow-xl transition-all duration-300">
                <div className="w-24 h-24 bg-[#0A1F44] rounded-full mx-auto mb-4 flex items-center justify-center">
                  <Scale className="w-12 h-12 text-[#D4AF37]" />
                </div>
                <h3 className="text-lg font-bold text-[#0A1F44] mb-2">{member.name}</h3>
                <p className="text-[#D4AF37] font-semibold text-sm mb-3">{member.role}</p>
                <p className="text-gray-600 text-sm leading-relaxed">{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#0A1F44] to-[#153356]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Protect Your Brand?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Let our expert team help you secure your intellectual property rights.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={openForm} className="liquid inline-block bg-[#D4AF37] hover:bg-[#B8941F] text-[#0A1F44] font-bold py-4 px-8 rounded-lg text-lg">
              Start Trademark Registration
            </button>
            <button onClick={openChat} className="liquid-white inline-block border-2 border-white text-white hover:bg-white hover:text-[#0A1F44] font-bold py-4 px-8 rounded-lg text-lg">
              Discuss with Attorney
            </button>
          </div>
        </div>
      </section>

      <Footer />
      
      <ConsultationForm 
        isOpen={isFormOpen} 
        onClose={closeForm}
        formType="consultation"
      />
    </div>
  );
};